import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Box } from "@mui/material";
import { useSnackbar } from "notistack";
import sh from "bundles/common/utils/sh";
import PipelineTable from "./pipeline/PipelineTable";
import { EMPTY_FILTERS } from "./pipeline/PipelineTableFilters";
import CreatePipelineDrawer from "./dashboard/CreatePipelineDrawer";

const PIPELINE_SEARCH_STORAGE_KEY = "pipelines_index_search_query";

const PipelinesIndexPage = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const authenticity_token = useSelector((state) => state.authenticity_token);
  const currentUser = useSelector((state) => state.current_internal_user);
  const isAdminOrCoordinator = currentUser?.role === "admin" || currentUser?.role === "coordinator";

  const [pipelines, setPipelines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState(
    () => sessionStorage.getItem(PIPELINE_SEARCH_STORAGE_KEY) || ""
  );
  const [filters, setFilters] = useState({ ...EMPTY_FILTERS, assigned_to_me: !isAdminOrCoordinator });
  const [owners, setOwners] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);

  const fetchPipelines = useCallback(() => {
    setLoading(true);
    const params = { page, per_page: perPage };
    if (searchQuery) params.q = searchQuery;
    if (filters.pipeline_type?.length === 1) params.pipeline_type = filters.pipeline_type[0];
    if (filters.owner_id?.length === 1) params.owner_id = filters.owner_id[0];
    if (filters.include_archived) params.include_archived = true;
    if (filters.assigned_to_me) params.assigned_to = currentUser?.id;
    if (filters.has_urgent) params.has_urgent = true;

    sh.get("/internal/profile_pipelines", { params })
      .then((res) => {
        setPipelines(res.data.pipelines || []);
        setTotalCount(res.data.total_count || 0);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [page, perPage, searchQuery, filters, currentUser?.id]);

  useEffect(() => {
    fetchPipelines();
  }, [fetchPipelines]);

  useEffect(() => {
    sessionStorage.setItem(PIPELINE_SEARCH_STORAGE_KEY, searchQuery);
  }, [searchQuery]);

  useEffect(() => {
    if (!isAdminOrCoordinator) return;
    sh.get("/internal/search/internal_users", { params: { all: true } })
      .then((res) => {
        const users = Array.isArray(res.data) ? res.data : res.data?.internal_users || [];
        setOwners(users);
      })
      .catch(() => {});
  }, [isAdminOrCoordinator]);

  const sortPinnedFirst = useCallback((list) =>
    [...list].sort((a, b) => {
      if (a.pinned && !b.pinned) return -1;
      if (!a.pinned && b.pinned) return 1;
      return new Date(b.created_at) - new Date(a.created_at);
    }),
  []);

  const handleTogglePin = useCallback((pipeline) => {
    const wasPinned = pipeline.pinned;
    const url = `/internal/profile_pipelines/${pipeline.id}/toggle_pin`;

    setPipelines((prev) =>
      sortPinnedFirst(prev.map((p) => (p.id === pipeline.id ? { ...p, pinned: !wasPinned } : p)))
    );

    sh.post(url, { authenticity_token, pinned: !wasPinned })
      .catch(() => {
        setPipelines((prev) =>
          sortPinnedFirst(prev.map((p) => (p.id === pipeline.id ? { ...p, pinned: wasPinned } : p)))
        );
      });
  }, [authenticity_token, sortPinnedFirst]);

  const handlePageChange = useCallback((newPage) => setPage(newPage), []);
  const handleRowsPerPageChange = useCallback((val) => { setPerPage(val); setPage(1); }, []);
  const handleFiltersChange = useCallback((next) => { setFilters(next); setPage(1); }, []);

  const handleUnarchive = useCallback((pipeline) => {
    setPipelines((prev) => prev.map((p) => (p.id === pipeline.id ? { ...p, archived: false } : p)));

    sh.post(`/internal/profile_pipelines/${pipeline.id}/unarchive`, { authenticity_token })
      .then(() => {
        enqueueSnackbar("Pipeline unarchived successfully.", { variant: "success" });
      })
      .catch((err) => {
        setPipelines((prev) => prev.map((p) => (p.id === pipeline.id ? { ...p, archived: true } : p)));
        if (err?.response?.status === 403) {
          enqueueSnackbar("You are not authorized to unarchive this pipeline.", { variant: "error" });
        }
      });
  }, [authenticity_token]);

  const handleArchive = useCallback((pipeline) => {
    if (!filters.include_archived) {
      setPipelines((prev) => prev.filter((p) => p.id !== pipeline.id));
    } else {
      setPipelines((prev) => prev.map((p) => (p.id === pipeline.id ? { ...p, archived: true } : p)));
    }

    sh.delete(`/internal/profile_pipelines/${pipeline.id}`, { data: { authenticity_token } })
      .then(() => {
        enqueueSnackbar("Pipeline archived successfully.", { variant: "success" });
      })
      .catch((err) => {
        setPipelines((prev) => {
          const exists = prev.find((p) => p.id === pipeline.id);
          if (exists) return prev.map((p) => (p.id === pipeline.id ? { ...p, archived: false } : p));
          return sortPinnedFirst([...prev, { ...pipeline, archived: false }]);
        });
        if (err?.response?.status === 403) {
          enqueueSnackbar("You are not authorized to archive this pipeline.", { variant: "error" });
        }
      });
  }, [authenticity_token, filters.include_archived, sortPinnedFirst]);

  const handleToggleJobOfferField = useCallback((pipelineId, pipelineJobOfferId, field, value) => {
    if (!pipelineJobOfferId) return;

    setPipelines((prev) =>
      prev.map((p) => {
        if (p.id !== pipelineId) return p;
        return {
          ...p,
          job_offers: (p.job_offers || []).map((jo) =>
            jo.pipeline_job_offer_id === pipelineJobOfferId ? { ...jo, [field]: value } : jo
          ),
        };
      })
    );

    const label = field === "urgent" ? "Urgent" : "On hold";

    sh.patch(`/internal/pipeline_job_offers/${pipelineJobOfferId}`, {
      [field]: value,
      authenticity_token,
    }).then(() => {
      enqueueSnackbar(`${label} ${value ? "enabled" : "disabled"}.`, { variant: "success" });
    }).catch(() => {
      setPipelines((prev) =>
        prev.map((p) => {
          if (p.id !== pipelineId) return p;
          return {
            ...p,
            job_offers: (p.job_offers || []).map((jo) =>
              jo.pipeline_job_offer_id === pipelineJobOfferId ? { ...jo, [field]: !value } : jo
            ),
          };
        })
      );
      enqueueSnackbar("Failed to update job offer field.", { variant: "error" });
    });
  }, [authenticity_token, enqueueSnackbar]);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: "linear-gradient(180deg, #F9FAFF 0%, #EEF0F6 100%)",
      }}
    >
      <PipelineTable
        pipelines={pipelines}
        onPipelineSelect={(pipeline) => {
          if (!pipeline?.id) return;
          navigate(`/internal/pipelines/${pipeline.id}`);
        }}
        initialQuery={searchQuery}
        onQueryChange={(q) => { setSearchQuery(q); setPage(1); }}
        onTogglePin={handleTogglePin}
        onArchive={handleArchive}
        onUnarchive={handleUnarchive}
        onCreateClick={() => setCreateOpen(true)}
        onToggleJobOfferField={handleToggleJobOfferField}
        filters={filters}
        onFiltersChange={handleFiltersChange}
        owners={owners}
        page={page}
        totalCount={totalCount}
        rowsPerPage={perPage}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
        loading={loading}
      />
      <CreatePipelineDrawer
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={() => {
          setCreateOpen(false);
          fetchPipelines();
          enqueueSnackbar("Pipeline created.", { variant: "success" });
        }}
      />
    </Box>
  );
};

export default PipelinesIndexPage;
